<template>
  <div class="registration-monthly-dashboard" v-loading="loading">
    <div class="dashboard-header">
      <p>{{ currentMonthLabel }} · 北京时间</p>
      <div class="month-tools">
        <el-date-picker v-model="month" class="month-picker" type="month" size="small"
          value-format="yyyy-MM" placeholder="选择月份" :clearable="false"
          @change="fetchMonthlyTrend"></el-date-picker>
        <el-button type="primary" icon="el-icon-refresh" size="small" :loading="loading"
          @click="fetchMonthlyTrend">刷新</el-button>
      </div>
    </div>

    <div class="metric-grid">
      <article class="metric-card metric-card--primary">
        <div class="metric-icon"><i class="el-icon-data-line"></i></div>
        <div class="metric-content">
          <div class="metric-label">本月报名订单数</div>
          <div class="metric-value">{{ monthlyTrend.totalRegistrationCount }}</div>
          <div class="metric-subtitle">已支付 + 免费报名</div>
        </div>
      </article>
      <article class="metric-card metric-card--previous">
        <div class="metric-icon"><i class="el-icon-date"></i></div>
        <div class="metric-content">
          <div class="metric-label">上月报名订单数</div>
          <div class="metric-value">{{ monthlyTrend.previousMonthRegistrationCount }}</div>
          <div class="metric-subtitle">{{ formatMonthLabel(monthlyTrend.previousMonth) }}</div>
        </div>
      </article>
      <article class="metric-card metric-card--year-on-year">
        <div class="metric-icon"><i class="el-icon-refresh"></i></div>
        <div class="metric-content">
          <div class="metric-label">报名订单数同比</div>
          <div class="metric-value rate-value" :class="getRateClass(monthlyTrend.yearOverYearRate)">
            <i :class="getRateIcon(monthlyTrend.yearOverYearRate)"></i>
            {{ formatRate(monthlyTrend.yearOverYearRate) }}
          </div>
          <div class="metric-subtitle">较 {{ formatMonthLabel(monthlyTrend.previousYearMonth) }}</div>
        </div>
      </article>
      <article class="metric-card metric-card--month-on-month">
        <div class="metric-icon"><i class="el-icon-sort"></i></div>
        <div class="metric-content">
          <div class="metric-label">报名订单数环比</div>
          <div class="metric-value rate-value" :class="getRateClass(monthlyTrend.monthOverMonthRate)">
            <i :class="getRateIcon(monthlyTrend.monthOverMonthRate)"></i>
            {{ formatRate(monthlyTrend.monthOverMonthRate) }}
          </div>
          <div class="metric-subtitle">较 {{ formatMonthLabel(monthlyTrend.previousMonth) }}</div>
        </div>
      </article>
    </div>

    <section class="chart-panel">
      <div class="panel-title">
        <div>
          <h3>近三年每月报名订单数</h3>
          <span>{{ yearlyChartSubtitle }}</span>
        </div>
        <el-tag size="small" effect="plain">单位：单</el-tag>
      </div>
      <div ref="yearlyChart" class="yearly-chart"></div>
    </section>

    <section class="chart-panel">
      <div class="panel-title">
        <div>
          <h3>每日报名订单数</h3>
          <span>{{ currentMonthLabel }} 每日报名订单数趋势</span>
        </div>
        <el-tag size="small" effect="plain">单位：单</el-tag>
      </div>
      <div ref="monthlyChart" class="monthly-chart"></div>
    </section>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import ThemeManager from '@/utils/theme.js';

const emptyMonthlyTrend = () => ({
  month: '',
  timezone: 'Asia/Shanghai',
  startAt: '',
  endAt: '',
  totalRegistrationCount: 0,
  previousMonth: '',
  previousMonthRegistrationCount: 0,
  previousYearMonth: '',
  previousYearRegistrationCount: 0,
  monthOverMonthRate: null,
  yearOverYearRate: null,
  dailyStats: [],
  yearlyMonthlyStats: []
});

export default {
  name: 'RegistrationMonthlyDashboard',
  data() {
    return {
      loading: false,
      month: this.$dayjs().format('YYYY-MM'),
      monthlyTrend: emptyMonthlyTrend(),
      monthlyChart: null,
      yearlyChart: null,
      unsubscribeTheme: null,
      resizeTimer: null
    };
  },
  computed: {
    currentMonthLabel() {
      return this.formatMonthLabel(this.month);
    },
    yearlyChartSubtitle() {
      const years = (this.monthlyTrend.yearlyMonthlyStats || []).map(item => item.year);
      return years.length ? `${years[0]}—${years[years.length - 1]} 年月度趋势对比` : '近三年月度趋势对比';
    }
  },
  mounted() {
    this.fetchMonthlyTrend();
    window.addEventListener('resize', this.handleResize);
    this.unsubscribeTheme = ThemeManager.subscribe(() => this.renderCharts());
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.unsubscribeTheme) this.unsubscribeTheme();
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    if (this.monthlyChart) this.monthlyChart.dispose();
    if (this.yearlyChart) this.yearlyChart.dispose();
    this.monthlyChart = null;
    this.yearlyChart = null;
  },
  methods: {
    fetchMonthlyTrend() {
      if (!this.month) return;
      this.loading = true;
      this.$axios.get('/form-result/monthly_registration_trend', {
        params: { month: this.month },
        headers: { 'x-auth-token': localStorage.getItem('bde_token') }
      }).then((res) => {
        this.monthlyTrend = { ...emptyMonthlyTrend(), ...(res.data.res || {}) };
        this.$nextTick(() => this.renderCharts());
      }).catch((err) => {
        this.$message.error(err.response?.data?.message || '报名订单月趋势加载失败');
        if (err.response?.data?.code === 'token_timeout') this.$router.push('/login');
      }).finally(() => {
        this.loading = false;
      });
    },
    formatMonthLabel(monthText) {
      return monthText ? this.$dayjs(`${monthText}-01`).format('YYYY年MM月') : '--';
    },
    formatRate(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
      const rate = Number(value);
      return `${rate > 0 ? '+' : ''}${rate.toFixed(2)}%`;
    },
    getRateClass(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return 'rate-unavailable';
      if (Number(value) > 0) return 'rate-positive';
      if (Number(value) < 0) return 'rate-negative';
      return 'rate-neutral';
    },
    getRateIcon(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return 'el-icon-minus';
      if (Number(value) > 0) return 'el-icon-top';
      if (Number(value) < 0) return 'el-icon-bottom';
      return 'el-icon-right';
    },
    handleResize() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (this.monthlyChart) this.monthlyChart.resize();
        if (this.yearlyChart) this.yearlyChart.resize();
      }, 120);
    },
    getThemeColor(name, fallback) {
      return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
    },
    getChartTheme() {
      const isDark = ThemeManager.isDark();
      return {
        axisColor: isDark ? '#8b949e' : '#909399',
        textColor: isDark ? '#e6edf3' : '#303133',
        splitColor: isDark ? '#30363d' : '#ebeef5',
        tooltipBackground: isDark ? '#20262e' : '#ffffff',
        tooltipBorder: isDark ? '#3c444d' : '#ebeef5'
      };
    },
    renderCharts() {
      this.renderYearlyChart();
      this.renderDailyChart();
    },
    renderYearlyChart() {
      if (!this.$refs.yearlyChart) return;
      if (!this.yearlyChart) this.yearlyChart = echarts.init(this.$refs.yearlyChart);
      const theme = this.getChartTheme();
      const isMobile = window.innerWidth <= 768;
      const brandColor = this.getThemeColor('--brand-primary', '#c04851');
      const yearlyStats = this.monthlyTrend.yearlyMonthlyStats || [];
      const palette = ['#3b82f6', '#8b5cf6', brandColor];
      const lineTypes = ['dotted', 'dashed', 'solid'];

      this.yearlyChart.setOption({
        color: palette,
        aria: { enabled: true, decal: { show: true } },
        tooltip: {
          trigger: 'axis',
          backgroundColor: theme.tooltipBackground,
          borderColor: theme.tooltipBorder,
          textStyle: { color: theme.textColor },
          axisPointer: { type: 'line' },
          valueFormatter: value => `${value} 单`
        },
        legend: {
          top: 0,
          right: isMobile ? 'center' : 8,
          textStyle: { color: theme.axisColor },
          itemWidth: 24,
          itemHeight: 10
        },
        grid: {
          left: isMobile ? 6 : 16,
          right: isMobile ? 12 : 24,
          top: isMobile ? 54 : 46,
          bottom: isMobile ? 24 : 18,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: Array.from({ length: 12 }, (_, index) => `${index + 1}月`),
          axisLine: { lineStyle: { color: theme.axisColor } },
          axisTick: { show: false },
          axisLabel: { color: theme.axisColor, interval: isMobile ? 1 : 0 }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLabel: { color: theme.axisColor },
          splitLine: { lineStyle: { color: theme.splitColor } }
        },
        series: yearlyStats.map((yearData, index) => ({
          name: `${yearData.year}年`,
          type: 'line',
          data: (yearData.months || []).map(item => Number(item.count || 0)),
          smooth: 0.25,
          symbol: index === yearlyStats.length - 1 ? 'circle' : 'emptyCircle',
          symbolSize: index === yearlyStats.length - 1 ? 7 : 6,
          lineStyle: {
            width: index === yearlyStats.length - 1 ? 3 : 2,
            type: lineTypes[index] || 'solid'
          },
          emphasis: { focus: 'series', lineStyle: { width: 4 } }
        }))
      }, true);
      this.yearlyChart.resize();
    },
    renderDailyChart() {
      if (!this.$refs.monthlyChart) return;
      if (!this.monthlyChart) this.monthlyChart = echarts.init(this.$refs.monthlyChart);
      const theme = this.getChartTheme();
      const isMobile = window.innerWidth <= 768;
      const brandColor = this.getThemeColor('--brand-primary', '#c04851');
      const brandRgb = this.getThemeColor('--brand-primary-rgb', '192, 72, 81');
      const stats = this.monthlyTrend.dailyStats || [];
      const labels = stats.map(item => item.label);
      const counts = stats.map(item => Number(item.count || 0));
      const lastLabelIndex = Math.max(labels.length - 1, 0);
      const labelInterval = isMobile
        ? index => index === 0 || index === lastLabelIndex || (index + 1) % 7 === 0
        : Math.max(0, Math.ceil(labels.length / 12) - 1);

      this.monthlyChart.setOption({
        color: [brandColor],
        aria: { enabled: true },
        tooltip: {
          trigger: 'axis',
          backgroundColor: theme.tooltipBackground,
          borderColor: theme.tooltipBorder,
          textStyle: { color: theme.textColor },
          axisPointer: { type: 'line' },
          formatter: (params) => {
            const item = params && params[0];
            return item ? `${item.name}<br/>报名订单数：${item.value} 单` : '';
          }
        },
        grid: {
          left: isMobile ? 6 : 16,
          right: isMobile ? 12 : 24,
          top: 26,
          bottom: isMobile ? 28 : 18,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: labels,
          axisLine: { lineStyle: { color: theme.axisColor } },
          axisTick: { show: false },
          axisLabel: {
            color: theme.axisColor,
            hideOverlap: true,
            interval: labelInterval,
            margin: isMobile ? 12 : 8,
            formatter: value => isMobile ? value.slice(3).replace('-', '/') : value
          }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLabel: { color: theme.axisColor },
          splitLine: { lineStyle: { color: theme.splitColor } }
        },
        series: [{
          name: '报名订单数',
          type: 'line',
          data: counts,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 3, color: brandColor },
          itemStyle: { color: brandColor, borderColor: theme.textColor, borderWidth: 1 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: `rgba(${brandRgb}, 0.26)` },
              { offset: 1, color: `rgba(${brandRgb}, 0.02)` }
            ])
          }
        }]
      }, true);
      this.monthlyChart.resize();
    }
  }
};
</script>

<style scoped>
.registration-monthly-dashboard { color: var(--text-primary); }
.dashboard-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.dashboard-header p,
.panel-title span,
.metric-subtitle {
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 13px;
}
.dashboard-header p { margin-top: 0; }
.month-tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}
.month-picker { width: 150px; }
.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}
.metric-card,
.chart-panel {
  border: 1px solid var(--border-lighter);
  border-radius: 10px;
  background: var(--bg-card);
  box-shadow: var(--shadow-light);
}
.metric-card {
  display: flex;
  align-items: center;
  gap: 14px;
  box-sizing: border-box;
  min-width: 0;
  min-height: 126px;
  padding: 20px;
}
.metric-card--primary { --metric-accent: var(--brand-primary); }
.metric-card--previous { --metric-accent: #3b82f6; }
.metric-card--year-on-year { --metric-accent: #8b5cf6; }
.metric-card--month-on-month { --metric-accent: #0f9f80; }
.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: var(--bg-table-hover);
  color: var(--metric-accent);
  font-size: 21px;
}
.metric-content { min-width: 0; }
.metric-label { color: var(--text-regular); font-size: 14px; }
.metric-value {
  margin-top: 6px;
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 700;
  line-height: 1.15;
  white-space: nowrap;
}
.rate-value {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 25px;
}
.rate-value i { font-size: 18px; font-weight: 700; }
.rate-positive { color: var(--color-success); }
.rate-negative { color: var(--color-danger); }
.rate-neutral,
.rate-unavailable { color: var(--text-secondary); }
.chart-panel {
  box-sizing: border-box;
  min-width: 0;
  margin-bottom: 16px;
  padding: 18px;
}
.panel-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.panel-title h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 17px;
  font-weight: 700;
}
.yearly-chart { width: 100%; height: 380px; }
.monthly-chart { width: 100%; height: 420px; }
@media (max-width: 1200px) {
  .metric-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 768px) {
  .dashboard-header { flex-direction: column; }
  .month-tools,
  .month-picker,
  .month-tools > .el-button { width: 100%; }
  .month-tools { flex-direction: column; align-items: stretch; }
  .metric-grid { grid-template-columns: 1fr; gap: 12px; }
  .metric-card,
  .chart-panel { padding: 14px; }
  .metric-card { min-height: 108px; }
  .metric-value { font-size: 24px; }
  .yearly-chart,
  .monthly-chart { height: 360px; }
}
</style>
